import React from 'react';
import { Mail, Phone, Download, Trash2 } from 'lucide-react';
import { deleteEmailSubscriber } from '../../services/api';

const SubscribersPanel = ({ subscribers, setSubscribers, showNotification }) => {
  const exportCSV = () => {
    const headers = ['Email', 'Phone', 'Source', 'Date'];
    const rows = subscribers.map(s => [
      s.email,
      s.phone || '',
      s.source || 'popup',
      new Date(s.subscribed_at).toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Exported to CSV!');
  };

  const handleDelete = async (subscriber) => {
    if (!window.confirm('Delete this subscriber?')) return;
    try {
      await deleteEmailSubscriber(subscriber.id);
      setSubscribers(prev => prev.filter(s => s.id !== subscriber.id));
      showNotification('Subscriber deleted');
    } catch (error) {
      showNotification('Failed to delete', 'error');
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800">Newsletter Subscribers</h2>
          <p className="text-slate-500 text-sm mt-1">
            {subscribers.length} {subscribers.length === 1 ? 'person has' : 'people have'} subscribed to your newsletter
          </p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          data-testid="export-subscribers-btn"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {subscribers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Mail className="w-16 h-16 mx-auto text-blue-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No subscribers yet</h3>
          <p className="text-slate-500">Newsletter signups from the popup will appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-sm font-medium text-slate-600">
            <div className="col-span-4">Email</div>
            <div className="col-span-3">Phone</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1"></div>
          </div>

          <div className="divide-y divide-slate-100">
            {subscribers.map((subscriber) => (
              <div 
                key={subscriber.id} 
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="md:col-span-4 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-slate-800 font-medium truncate">{subscriber.email}</span>
                </div>
                <div className="md:col-span-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400 flex-shrink-0 md:hidden" />
                  <span className="text-slate-600">
                    {subscriber.phone || <span className="text-slate-400 italic">No phone</span>}
                  </span>
                </div>
                <div className="md:col-span-2 flex items-center">
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full capitalize">
                    {subscriber.source || 'popup'}
                  </span>
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <span className="text-slate-500 text-sm">
                    {new Date(subscriber.subscribed_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <button
                    onClick={() => handleDelete(subscriber)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-subscriber-${subscriber.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribersPanel;
